/*
 * This file is part of glowingblue/orell-facade.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { slug } from '../common';
import { extend } from 'flarum/common/extend';
import ItemList from 'flarum/common/utils/ItemList';
import { components } from '@glowingblue-orell-thalia-common';
import HomeFacadeBlock from './components/HomeFacadeBlock';
import HomeSlider from './components/HomeSlider';
import ReadingRoundBlock from './components/ReadingRoundBlock';

const t = app.translator.trans.bind(app.translator);
const prfx = `${slug}.forum.home`;

// Every name below is load-bearing: Flarum renders `<li class="item-<name>">` and
// `resources/less/home.less` selects on those classes.

/**
 * Adds a header and its facade blocks to `items`. The header is dropped once every block in the set
 * is switched off, which the backend reports per position on `app.noHomeFacadeBlock`.
 */
const addFacadeBlocks = (items, { headerName, headerLocaleKey, blocks }) => {
	if (app.noHomeFacadeBlock && blocks.some((block) => !app.noHomeFacadeBlock[block.position])) {
		items.add(
			headerName,
			<div className="HomeFacadeBlock-header">
				<h2>{t(`${prfx}.${headerLocaleKey}`)}</h2>
			</div>,
			6
		);
	}

	blocks.forEach((block) => items.add(block.name, <HomeFacadeBlock position={block.position} />, block.priority));
};

const addFacadeBlockGroup = (groups, { name, priority, headerName, headerLocaleKey, blocks }) => {
	const items = new ItemList();

	addFacadeBlocks(items, { headerName, headerLocaleKey, blocks });

	groups.add(name, items, priority);
};

// The group and its single item share a name — `home.less` selects on both.
const addSponsorGroup = (groups, { name, position, priority }) => {
	groups.add(name, new ItemList().add(name, <HomeFacadeBlock position={position} />), priority);
};

/*
 * Home page order, decided by group priority alone. Groups owned elsewhere are marked:
 *
 *   100  welcome                           common              hero slider, first & second blocks
 *    96  sponsor-above-reviews
 *    95  reviews                           orell-rezensionen
 *    94  sponsor-below-reviews
 *    92  new-facades                                           fifth & sixth blocks
 *    90  latest                            common              reading rounds, discussion list
 *    85  sponsor-between-community-events
 *    80  facades                                               third & fourth blocks ("Events")
 *    70  categories                        common
 *    50  leaderboards                      common
 */
export default () => {
	extend(components.HomePage.prototype, 'welcomeBlocks', function (items) {
		// Above the welcome block (100), as on the old gb-core home page.
		items.add('slider', <HomeSlider />, 200);

		addFacadeBlocks(items, {
			headerName: 'facade-blocks-header',
			headerLocaleKey: 'one_and_two_block',
			blocks: [
				{ name: 'facade-block-first', position: 'first_block', priority: 5 },
				{ name: 'facade-block-second', position: 'second_block', priority: 4 },
			],
		});
	});

	extend(components.HomePage.prototype, 'latestBlocks', function (items) {
		items.add('reading-rounds', <ReadingRoundBlock />, 200);
	});

	extend(components.HomePage.prototype, 'blockGroups', function (groups) {
		addSponsorGroup(groups, { name: 'sponsor-above-reviews', position: 'sponsor_above_reviews', priority: 96 });
		addSponsorGroup(groups, { name: 'sponsor-below-reviews', position: 'sponsor_below_reviews', priority: 94 });

		addFacadeBlockGroup(groups, {
			name: 'new-facades',
			priority: 92,
			// Mismatched on purpose: `home.less` selects `item-new-block-facades-header`.
			headerName: 'new-block-facades-header',
			headerLocaleKey: 'five_and_six_block',
			blocks: [
				{ name: 'facade-block-fifth', position: 'fifth_block' },
				{ name: 'facade-block-sixth', position: 'sixth_block' },
			],
		});

		addSponsorGroup(groups, { name: 'sponsor-between-community-events', position: 'sponsor_between_community_events', priority: 85 });

		addFacadeBlockGroup(groups, {
			name: 'facades',
			priority: 80,
			headerName: 'facade-blocks-header',
			headerLocaleKey: 'three_and_four_block',
			blocks: [
				{ name: 'facade-block-third', position: 'third_block' },
				{ name: 'facade-block-fourth', position: 'fourth_block' },
			],
		});
	});
};
